import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { projectsService, type Project } from '../services/projectsService';
import { navigationService } from '../services/navigationService';

interface ProjectContextType {
  // Project state
  projects: Project[];
  selectedProject: Project | null;
  projectsLoading: boolean;
  
  // Actions
  loadProjects: () => Promise<void>;
  updateProjectCompanion: (projectId: string, companionData: any) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: React.ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const location = useLocation();
  
  // Extract project ID from pathname using navigationService
  const projectId = navigationService.extractProjectId(location.pathname);

  const loadProjects = async () => {
    console.log('[ProjectContext] Loading projects...');
    setProjectsLoading(true);
    try {
      const projectsData = await projectsService.getAllProjects();
      console.log('[ProjectContext] Loaded projects:', projectsData);
      
      setProjects(projectsData);
      
      // Update selected project if it exists in the new data
      if (selectedProject) {
        const updatedSelectedProject = projectsData.find(p => p.project_uuid === selectedProject.project_uuid);
        if (updatedSelectedProject) {
          console.log('[ProjectContext] Updated selected project:', updatedSelectedProject);
          setSelectedProject(updatedSelectedProject);
        }
      }
    } catch (error) {
      console.error('[ProjectContext] Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
      console.log('[ProjectContext] Projects loading completed');
    }
  };

  const updateProjectCompanion = async (projectId: string, companionData: any) => {
    console.log('[ProjectContext] Updating project companion:', { projectId, companionData });
    try {
      const updatedProject = await projectsService.updateProject(projectId, { companion_data: companionData });
      console.log('[ProjectContext] Project companion updated:', updatedProject);
      
      // Update the projects list
      setProjects(prev => prev.map(p => 
        p.project_uuid === projectId ? updatedProject : p
      ));
      
      // Update selected project if it's the one being updated
      if (selectedProject && selectedProject.project_uuid === projectId) {
        setSelectedProject(updatedProject);
      }
    } catch (error) {
      console.error('[ProjectContext] Error updating project companion:', error);
      throw error;
    }
  };

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Auto-select project based on URL
  useEffect(() => {
    console.log('[ProjectContext] Auto-select effect triggered:', { 
      projectId, 
      projectsCount: projects.length, 
      pathname: location.pathname,
      selectedProjectId: selectedProject?.project_uuid 
    });
    
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.project_uuid === projectId);
      console.log('[ProjectContext] Found project for URL:', project);
      
      if (project) {
        // Always set the project from URL if it exists, even if selectedProject is already set
        // This ensures we get the latest data after refresh
        if (!selectedProject || selectedProject.project_uuid !== project.project_uuid) {
          console.log('[ProjectContext] Setting new selected project:', project);
          setSelectedProject(project);
        } else if (selectedProject && selectedProject.project_uuid === project.project_uuid) {
          // Update with fresh data if it's the same project but potentially stale data
          console.log('[ProjectContext] Updating selected project with fresh data:', project);
          setSelectedProject(project);
        }
      } else {
        console.log('[ProjectContext] Project not found in projects list for ID:', projectId);
      }
    } else if (!location.pathname.includes('/projects/')) {
      // Clear selected project when not on project routes
      if (selectedProject) {
        console.log('[ProjectContext] Clearing selected project - not on project route');
        setSelectedProject(null);
      }
    }
  }, [projectId, projects, location.pathname]);

  const contextValue: ProjectContextType = {
    projects,
    selectedProject,
    projectsLoading,
    loadProjects,
    updateProjectCompanion,
  };

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};
